/**
 * HamburgerDrawer — persistent left rail (Claude Desktop style).
 *
 * When expanded, the rail takes ~280px next to the page content. When
 * collapsed, it shrinks to width 0 (still mounted; query stays gated).
 * The hamburger button in ChatChrome toggles expanded/collapsed.
 *
 * #1393 (2026-09-05): the rail is the Recents list and ONE row of five icons.
 * Everything that used to stack inside it — the inline search field, the
 * Recent/Topics chips, the Aa text-size menu, and the whole "This
 * conversation" block (End session, range chips, Download, Copy, Delete) —
 * now lives behind an icon in a modal: New · Search (⌘K) · View options ·
 * Download · End session. The list gets the vertical space back; the modals
 * get room to explain themselves. Behaviour underneath is unchanged: #473
 * Recent/Topics, #553 title search, #270/#269 range presets, #554 formats,
 * #680 per-row export, #293 one-shot End.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, MoreHorizontal, LogOut, Download, Search, SlidersHorizontal, X, type LucideIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@core/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@core/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@core/ui/dialog';
import { Button } from '@core/ui/button';
import { cn } from '@core/lib/utils';
import { chatConfig, chatCopy } from '../../config';
import type { ConversationSummary } from '../services/stevensV4Service';
import type { ServerExportFormat } from '../services/conversationExportService';
import { countTimestampsInPreset, type SlicePreset } from '../utils/time';
import { groupConversationsByTopic } from '../utils/conversation-topics';
import { groupByRecency, recencyRowMeta } from '../utils/recency-groups';
import { useFontScale, type FontScale } from '@core/hooks/useFontScale';

// TRF delta (shell stacking contract, ui-ux-guideline §2.10 rule 3): every
// layer opened from inside the drawer — the row menu, the modals and their
// scrims, the delete confirm — takes the z-[70] slot. On a phone the drawer
// is a z-[60] overlay above the z-50 header, so a z-50 layer would open
// behind it and its scrim.
const hostedLayer = 'z-[70]';

// Backlog 2026-07-13 (a11y font-size toggle): Small/Medium/Large — scoped to
// body/content text only via --content-font-scale (tokens.css); see
// useFontScale.ts. Medium is the default and is checkmarked (radio dot) below.
// #2109 R3-B24: the labels come from the copy pack (`drawer.fontScales`).
const FONT_SCALES: ReadonlyArray<FontScale> = ['sm', 'md', 'lg'];

// #262: Resize handle sizing. Matches the localStorage-persistence convention
// used for conversationId in useStevensV4.ts (lazy useState initializer reads
// on mount, useEffect/handler writes on change).
const SIDEBAR_WIDTH_STORAGE_KEY = 'stevens-v4-sidebar-width';
const MIN_SIDEBAR_WIDTH = 220;
const MAX_SIDEBAR_WIDTH = 420;
const DEFAULT_SIDEBAR_WIDTH = 280;

function clampSidebarWidth(width: number): number {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));
}

// #264: Recents list title. Preferred source is the AI-generated
// post-session summary (backend joins MongoDB session_summaries onto
// conversationId — see GET /conversations in stevens-v4.js). Falls back to
// the raw first user message, cleaned up and truncated at a word boundary,
// when no summary is available yet (conversation hasn’t reached a
// digest-close event) or for any other reason the join comes back empty.
const TITLE_MAX_LENGTH = 60;

/** Strip common markdown syntax and bare URLs from a raw text snippet, collapse whitespace. */
function cleanupSnippetText(text: string): string {
  return text
    .replace(/https?:\/\/\S+/gi, '') // bare URLs
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // markdown links -> link text only
    .replace(/[*_`#>]+/g, '') // bold/italic/code/heading/quote markers
    .replace(/\s+/g, ' ')
    .trim();
}

/** Truncate at the nearest earlier word boundary within maxLength (never cuts mid-word). */
function truncateAtWordBoundary(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const slice = text.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > maxLength * 0.4 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
}

/**
 * The row's display title.
 *
 * #1832: `withChannelBadge` decides whether the untitled fallback carries the
 * channel word. The RENDERED row already shows the channel as its own bordered
 * chip on the meta line, so a row that passed the badge here read "Sep 6 ·
 * Voice" over "9:49 PM [Voice]" — the same word twice. The chip is the one that
 * survives, so the row derives its title WITHOUT the badge. The search filter
 * still derives it WITH the badge, so typing "voice" keeps finding untitled
 * voice threads even though the word is no longer in the visible title line.
 */
function deriveConversationTitle(
  c: ConversationSummary,
  { withChannelBadge = false }: { withChannelBadge?: boolean } = {}
): string {
  // #264: dedicated AI-generated title wins when the backend provides one.
  const title = c.title?.trim();
  if (title) return truncateAtWordBoundary(title, TITLE_MAX_LENGTH);

  const summary = c.summary?.trim();
  if (summary) return truncateAtWordBoundary(summary, TITLE_MAX_LENGTH);

  const firstMessage = c.firstUserMessage ? cleanupSnippetText(c.firstUserMessage) : '';
  if (firstMessage) return truncateAtWordBoundary(firstMessage, TITLE_MAX_LENGTH);

  // #1308 (§33.2): the deterministic fallback — date, plus channel where
  // marked. Never "Untitled", never "New conversation".
  const drawerCopy = chatCopy().drawer;
  const timestamp = c.lastActivity || c.createdAt;
  return drawerCopy.untitledFallback(
    timestamp ? new Date(timestamp) : new Date(),
    withChannelBadge && c.channel ? drawerCopy.channelBadges[c.channel] : undefined
  );
}

/**
 * Export formats offered by the drawer. md/txt are client-side; docx (#268)
 * and pdf (#275) are server-generated; 'print' (#1308, CP-36) is the
 * client-side browser-print path — no server renderer.
 */
export type ConversationExportFormat = 'md' | 'txt' | 'docx' | 'pdf' | 'print';

// #473: Recent (flat, reverse-chronological — default) vs Topics (grouped by
// primary keyTopic). Persisted per the SIDEBAR_WIDTH localStorage pattern.
const RECENTS_VIEW_STORAGE_KEY = 'stevens-v4-recents-view';
type RecentsView = 'recent' | 'topics';

// #2109 R3-B24: the labels come from the copy pack (`drawer.views`).
const RECENTS_VIEWS: ReadonlyArray<RecentsView> = ['recent', 'topics'];

function loadStoredRecentsView(): RecentsView {
  try {
    const stored = localStorage.getItem(RECENTS_VIEW_STORAGE_KEY);
    if (stored === 'recent' || stored === 'topics') return stored;
  } catch {
    // localStorage unavailable (private mode / quota) — fall through to default
  }
  return 'recent';
}

// #270: time-based slice presets for export/copy.
// #269: 'Custom' enters click-to-mark selection mode in the message list —
// the count shown/gated here comes from customSelectionCount, not timestamps.
// #2109 R3-B24: the labels come from the copy pack (`drawer.slices`).
const SLICE_PRESETS: ReadonlyArray<SlicePreset> = ['everything', 'today', 'week', 'custom'];

export interface HamburgerDrawerProps {
  isExpanded: boolean;
  /**
   * TRF delta (chat UX brief 2026-09-07, change #5): a host-owned control
   * pinned to the RIGHT end of the icon row. TRF puts the one remaining
   * hamburger — the rail's own collapse toggle — there, so the page carries a
   * single hamburger instead of two stacked in the header.
   */
  railTrailingAction?: ReactNode;
  isMobile: boolean;
  currentConversationId: string | undefined;
  hasMessages: boolean;
  /**
   * #293: true when the current conversation has already been ended
   * (status 'completed'). Hides the "End session" action — ending is a
   * one-shot event — while keeping copy/export/delete available.
   */
  sessionEnded?: boolean;
  onNewConversation: () => void;
  /**
   * #471: fired after a row-delete removes the CURRENTLY LOADED thread.
   * chat.tsx owns the reset (resetConversation + navigate to a blank
   * composer) — the drawer must not navigate on its own, because the chat
   * page never remounts and useStevensV4's prop-sync ignores a missing
   * ?conversationId, which would leave the deleted thread’s messages and
   * sessionId live in state.
   */
  onActiveConversationDeleted: () => void;
  onClose: () => void;
  onEnd: () => void;
  onCopy: (preset: SlicePreset) => void;
  onDownload: (format: ConversationExportFormat, preset: SlicePreset) => void;
  onDelete: () => void;
  copied: boolean;
  copying: boolean;
  /** True while the server-side .docx export is in flight (#268). */
  exportingDocx: boolean;
  /** True while the server-side .pdf export is in flight (#275). */
  exportingPdf: boolean;
  /**
   * Epoch-ms timestamps of the loaded messages (#270) — used to detect an
   * empty slice so we disable export instead of emitting an empty file.
   */
  messageTimestamps: number[];
  /**
   * #269: notifies the parent whenever the slice preset changes (user click
   * or the #472 per-thread auto-reset) so chat.tsx can enter/leave the
   * custom selection mode on the message list.
   */
  onSlicePresetChange?: (preset: SlicePreset) => void;
  /**
   * #269: number of messages covered by the current custom selection.
   * Gates the export/copy actions when the 'Custom' preset is active.
   */
  customSelectionCount?: number;
  /**
   * #680: export any conversation straight from its Recents row. The server
   * export endpoint is authorized per-conversation (ownership enforced in the
   * Cypher), so any id in this list is safe to pass. Optional, so other
   * consumers of the drawer are unaffected.
   *
   * docx/pdf only: the md/txt path is synchronous off the ACTIVE thread’s
   * in-memory messages and has no in-flight state, so a non-active thread
   * would need a fetch and would read as a dead click.
   */
  onExportConversation?: (conversationId: string, format: ServerExportFormat) => void;
  /** Conversation whose row export is currently in flight, if any. */
  exportingConversationId?: string | null;
  /**
   * TRF (#1731): when provided, a row pick calls this instead of navigating
   * to `routes.chat?conversationId=` — the host owns its thread URL shape
   * (TRF: /chat/:conversationId, #1190).
   */
  onSelectConversation?: (conversationId: string) => void;
  /**
   * TRF delta (E2E wave 2): host-owned `(?)` markers for the drawer's unruled
   * choices. chat-core cannot import the host's marker, so the host passes the
   * rendered nodes: `recents` beside the Recents label (row menu and title
   * language), `rename` in the rename dialog, `deleteDialog` beside the
   * delete warning. Omitted, nothing is drawn.
   */
  decisionMarkers?: { recents?: ReactNode; rename?: ReactNode; deleteDialog?: ReactNode };
  /**
   * TRF delta (#2266): the edge the rail is anchored to. TRF's companion
   * thread lives on the parent home, where the history opens as a collapsed
   * RIGHT column (desktop) and a right-edge overlay (phone). `'left'`, the
   * default, is upstream's rail exactly.
   */
  side?: 'left' | 'right';
}

export function HamburgerDrawer(props: HamburgerDrawerProps) {
  const {
    isExpanded,
    railTrailingAction,
    isMobile,
    currentConversationId,
    hasMessages,
    sessionEnded = false,
    onNewConversation,
    onActiveConversationDeleted,
    onClose,
    onEnd,
    onCopy,
    onDownload,
    onDelete,
    copied,
    copying,
    exportingDocx,
    exportingPdf,
    messageTimestamps,
    onSlicePresetChange,
    customSelectionCount = 0,
    onExportConversation,
    exportingConversationId = null,
    decisionMarkers,
    side = 'left',
  } = props;
  const anchoredRight = side === 'right';
  // #2109 R3-B24: every drawer string arrives through the copy pack.
  const dc = chatCopy().drawer;

  // #270: which time slice the export/copy actions operate on. "Everything"
  // is the default; count is recomputed against local-time boundaries on
  // every render so a slice that empties out (e.g. past midnight) disables
  // the actions rather than emitting an empty file.
  // #269: 'custom' is index-based â its count comes from the parent’s
  // selection state, not from timestamps.
  const [slicePreset, setSlicePreset] = useState<SlicePreset>('everything');
  const sliceCount =
    slicePreset === 'custom'
      ? customSelectionCount
      : countTimestampsInPreset(messageTimestamps, slicePreset);
  const sliceEmpty = slicePreset !== 'everything' && sliceCount === 0;

  // #269: keep the parent informed so it can toggle selection mode. Stable
  // ref so the #472 reset effect below stays keyed to the thread only.
  const onSlicePresetChangeRef = useRef(onSlicePresetChange);
  onSlicePresetChangeRef.current = onSlicePresetChange;
  const changeSlicePreset = (preset: SlicePreset) => {
    setSlicePreset(preset);
    onSlicePresetChangeRef.current?.(preset);
  };

  // #472: the preset is per-thread UI state, but the drawer stays mounted
  // across thread switches (it only collapses to w-0). Without this reset an
  // old conversation opened after picking "Today" elsewhere lands on a stale
  // preset showing "No messages in this range".
  useEffect(() => {
    setSlicePreset('everything');
    onSlicePresetChangeRef.current?.('everything');
  }, [currentConversationId]);

  // Backlog 2026-07-13: Aa text-size control — see useFontScale.ts.
  const { fontScale, setFontScale } = useFontScale();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: textConversations, isLoading, isError, refetch } = useQuery({
    queryKey: ['conversationList'],
    queryFn: () => chatConfig().endpoints.conversations.list(50, 0),
    enabled: isExpanded,
    staleTime: 30_000,
  });

  // #1308 (C-5): voice conversations live under a different Neo4j label and a
  // different endpoint; fetched separately and merged below so phone and web
  // voice sessions sit in the same recency-ordered history. Fail-soft (the
  // service returns [] on any failure) — voice trouble never blanks text
  // history.
  const { data: voiceConversations } = useQuery({
    queryKey: ['voiceConversationList'],
    queryFn: () => chatConfig().endpoints.conversations.listVoice(50),
    enabled: isExpanded,
    staleTime: 30_000,
  });

  const conversations = useMemo(() => {
    // Gate on the TEXT query: voice rows are garnish, and the fail-soft voice
    // fetch settles as [] on any failure — treating that as "loaded" made
    // "No conversations yet." flash before the real list arrived (review
    // finding, 2026-08-31). Cross-list dedupe guards the two label spaces
    // ever surfacing the same conversationId (duplicate React keys).
    if (!textConversations) return textConversations;
    // Inline dedupe (not the service’s dedupeConversationsById): page-level
    // suites vi.mock the whole service module, and a new value import from
    // it would break every one of those mocks.
    const seen = new Set<string>();
    const merged = [...textConversations, ...(voiceConversations ?? [])].filter((c) => {
      if (seen.has(c.conversationId)) return false;
      seen.add(c.conversationId);
      return true;
    });
    merged.sort(
      (a, b) => (b.lastActivity || b.createdAt || 0) - (a.lastActivity || a.createdAt || 0)
    );
    return merged;
  }, [textConversations, voiceConversations]);

  const [rowPendingDelete, setRowPendingDelete] = useState<string | null>(null);
  // The row-actions button that opened the last row menu. The menu item that
  // opens the delete confirm unmounts with the menu, so the confirm hands focus
  // back to this button instead (WAI-ARIA dialog pattern: return to the opener).
  const rowMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const rememberRowMenuTrigger = (e: { currentTarget: HTMLButtonElement }) => {
    rowMenuTriggerRef.current = e.currentTarget;
  };
  const [isDeletingRow, setIsDeletingRow] = useState(false);

  // #2404: Rename lives in the row menu beside Delete, through the host's
  // optional seam. The draft starts from the STORED title only — a derived
  // fallback (first message, date) would be saved back as if it were chosen.
  const [rowPendingRename, setRowPendingRename] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [isRenamingRow, setIsRenamingRow] = useState(false);
  const [renameFailed, setRenameFailed] = useState(false);
  const renameConversation = chatConfig().endpoints.conversations.rename;
  const openRename = (c: ConversationSummary) => {
    setRenameDraft(c.title ?? '');
    setRenameFailed(false);
    setRowPendingRename(c.conversationId);
  };

  // #553: client-side text filter over the loaded recents (V1 scope — the
  // drawer only fetches the 50 most-recent conversations, so a substring
  // filter over what’s loaded covers "I know it’s recent, find it fast";
  // full-history search via a backend `search` param is the V2 follow-up).
  // Matches on the DISPLAYED title (deriveConversationTitle fallback chain),
  // case-insensitive. Applied to both the Recent and Topics views. The
  // drawer stays mounted while collapsed, so reset on close via isExpanded.
  const [searchQuery, setSearchQuery] = useState('');

  // #1393: the five-icon row. Each modal owns its own open flag; the drawer
  // stays mounted while collapsed, so collapsing closes them all (a modal
  // must never outlive the rail it was opened from).
  const [searchOpen, setSearchOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  useEffect(() => {
    if (!isExpanded) {
      setSearchOpen(false);
      setViewOpen(false);
      setExportOpen(false);
    }
  }, [isExpanded]);
  // #553's "reset on close" now keys off the search modal, since that is
  // where the field lives.
  useEffect(() => {
    if (!searchOpen) setSearchQuery('');
  }, [searchOpen]);
  // ⌘K / Ctrl+K opens search while the rail is showing — the shortcut every
  // history-search modal (claude.ai, Slack, Linear) answers to.
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);
  // TRF delta (#2617): on a phone the open drawer is an overlay with a scrim,
  // and like every other overlay in the app (the More drawer, the dialogs)
  // Escape closes it. A layer opened from inside the drawer answers Escape
  // first: Radix layers (the row menu, the modals) dismiss on a capture-phase
  // document listener and mark the event handled, so this window listener
  // sees `defaultPrevented` and leaves the drawer open.
  useEffect(() => {
    if (!isMobile || !isExpanded) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (searchOpen || viewOpen || exportOpen) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, isExpanded, searchOpen, viewOpen, exportOpen, onClose]);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleConversations =
    conversations && normalizedQuery
      ? conversations.filter((c) =>
          // #1832: match WITH the channel badge — the chip is on screen even
          // though the title line no longer repeats it, so "voice" must still
          // find an untitled voice thread.
          deriveConversationTitle(c, { withChannelBadge: true })
            .toLowerCase()
            .includes(normalizedQuery),
        )
      : conversations;

  // #473: Recent | Topics toggle, persisted like SIDEBAR_WIDTH (lazy read on
  // mount, write on change).
  const [recentsView, setRecentsView] = useState<RecentsView>(loadStoredRecentsView);
  const handleRecentsViewChange = (view: RecentsView) => {
    setRecentsView(view);
    try {
      localStorage.setItem(RECENTS_VIEW_STORAGE_KEY, view);
    } catch {
      // localStorage unavailable â the choice just won’t persist
    }
  };

  // #262: Draggable resize handle. Width is clamped to a sane range and
  // persisted to localStorage so it survives reload, matching the
  // conversationId persistence pattern in useStevensV4.ts.
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed)) return clampSidebarWidth(parsed);
      }
    } catch {
      // localStorage unavailable (private mode / quota) — fall through to default
    }
    return DEFAULT_SIDEBAR_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = sidebarWidthRef.current;
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    // Prevent text-selection thrash elsewhere on the page while dragging.
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const handleMouseMove = (e: MouseEvent) => {
      // A right-anchored rail grows as the pointer moves LEFT (#2266).
      const moved = e.clientX - dragStartXRef.current;
      const delta = anchoredRight ? -moved : moved;
      setSidebarWidth(clampSidebarWidth(dragStartWidthRef.current + delta));
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      try {
        localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidthRef.current));
      } catch {
        // localStorage unavailable (private mode / quota) â width just won’t persist
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, anchoredRight]);

  const handleConfirmRowDelete = async () => {
    if (!rowPendingDelete) return;
    setIsDeletingRow(true);
    try {
      const ok = await chatConfig().endpoints.conversations.delete(rowPendingDelete);
      if (!ok) {
        console.error('[HamburgerDrawer] deleteConversation returned false', {
          conversationId: rowPendingDelete,
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['conversationList'] });
      if (rowPendingDelete === currentConversationId) {
        // #471: deleting the loaded thread must also reset the chat page’s
        // state (messages/sessionId) — chat.tsx owns that reset.
        onActiveConversationDeleted();
      }
    } finally {
      setIsDeletingRow(false);
      setRowPendingDelete(null);
    }
  };

  const handleConfirmRename = async () => {
    const title = renameDraft.trim();
    if (!rowPendingRename || !renameConversation || !title) return;
    setIsRenamingRow(true);
    setRenameFailed(false);
    let ok = false;
    try {
      ok = await renameConversation(rowPendingRename, title);
    } catch (err) {
      console.error('[HamburgerDrawer] renameConversation failed', { conversationId: rowPendingRename, err });
    }
    if (ok) await queryClient.invalidateQueries({ queryKey: ['conversationList'] });
    setIsRenamingRow(false);
    if (ok) setRowPendingRename(null);
    else setRenameFailed(true);
  };

  // Shared row renderer for both the flat Recent list and the Topics
  // sections (#473) — one conversation, one row, either way.
  // #1308: ONE clock per render, shared by grouping and row meta — separate
  // `new Date()` defaults could bucket a row under "Today" while its meta
  // renders a weekday when a render straddles midnight (review finding).
  const renderNow = useMemo(() => new Date(), [conversations]);  // eslint-disable-line react-hooks/exhaustive-deps

  const renderConversationRow = (c: ConversationSummary, opts: { inDialog?: boolean } = {}) => {
    const isActive = c.conversationId === currentConversationId;
    const title = deriveConversationTitle(c);
    const ts = c.lastActivity || c.createdAt;
    // #1308 (O-4/§31.3): bucket-aware meta — clock time today, weekday +
    // time this week, date + time earlier (the time keeps same-day untitled
    // rows distinguishable).
    const meta = recencyRowMeta(ts, renderNow, chatCopy().locale);
    // #1308 (C-5): trailing channel badge; web is deliberately unmarked, and
    // channels absent from the brand’s badge map render nothing.
    const channelBadge = c.channel ? dc.channelBadges[c.channel] : undefined;
    const isVoiceRow = c.sessionType === 'voice';
    // Same rule as the Download modal: a server format is offered only where
    // an endpoint answers for it (#1731 phase 7).
    const rowServerFormats = chatConfig().serverExportFormats;
    return (
      <li key={c.conversationId} className="group relative">
        <button
          type="button"
          aria-current={isActive ? 'true' : undefined}
          onClick={() => {
            // #1302 Guard B: navigation targets are brand routes, injected
            // via BrandChatConfig.routes.
            if (props.onSelectConversation) props.onSelectConversation(c.conversationId);
            else navigate(`${chatConfig().routes.chat}?conversationId=${encodeURIComponent(c.conversationId)}`);
            // #1393: a search hit closes the search modal it was picked from.
            if (opts.inDialog) setSearchOpen(false);
            if (isMobile) onClose();
          }}
          className={cn(
            'flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left transition-colors',
            isActive
              ? 'bg-chat-surface-hover border-l-2 border-chat-accent pl-[10px]'
              : 'hover:bg-chat-surface-hover',
          )}
        >
          <span className="w-full truncate pe-7 text-[length:calc(13rem/17*var(--content-font-scale))] text-chat-text [@media(pointer:coarse)]:pe-12">
            {title}
          </span>
          {(meta || channelBadge) && (
            <span className="flex w-full items-center gap-1.5 text-[length:calc(11rem/17*var(--content-font-scale))] text-chat-text-muted">
              {meta}
              {channelBadge && (
                <span className="rounded border border-chat-border px-1 text-[length:calc(10rem/17*var(--content-font-scale))]">
                  {channelBadge}
                </span>
              )}
            </span>
          )}
        </button>
        {/* #1393: search results are pick-only — the row actions stay on the
            rail rows, where the thread can be found again after the modal. */}
        {!opts.inDialog && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={dc.rowActions}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={rememberRowMenuTrigger}
              onKeyDown={rememberRowMenuTrigger}
              // #2406: revealed by opacity, never display:none — a hover-only
              // `hidden … group-hover:block` button was unreachable by touch
              // and by Tab. Devices that cannot hover always show it, and a
              // coarse pointer gets a 44 px target.
              className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center rounded p-1 text-chat-text-muted opacity-0 transition-[color,background-color,opacity] hover:bg-chat-surface-hover hover:text-chat-text group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
            >
              <MoreHorizontal size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={cn('w-44', hostedLayer)}>
            {onExportConversation && rowServerFormats.includes('docx') && (
              <>
                <DropdownMenuItem
                  disabled={exportingConversationId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExportConversation(c.conversationId, 'docx');
                  }}
                >
                  {exportingConversationId === c.conversationId ? dc.exporting : dc.exportDocx}
                </DropdownMenuItem>
                {rowServerFormats.includes('pdf') && (
                  <DropdownMenuItem
                    disabled={exportingConversationId !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExportConversation(c.conversationId, 'pdf');
                    }}
                  >
                    {dc.exportPdf}
                  </DropdownMenuItem>
                )}
              </>
            )}
            {renameConversation && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  openRename(c);
                }}
              >
                {dc.rename}
              </DropdownMenuItem>
            )}
            {/* #1308: row delete goes through the Tiger delete route, which
                does not cover voice labels — hide it on voice rows rather
                than offer an action that must fail. */}
            {!isVoiceRow && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setRowPendingDelete(c.conversationId);
                }}
                className="text-destructive focus:text-destructive"
              >
                {dc.delete}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </li>
    );
  };

  // #1393: the rail’s five icons share one grammar â 32px ghost squares on
  // the 8px control radius, muted ink that fills on hover. `RailIcon` is a
  // render helper (not a component) so it can read the closure’s state
  // without a prop pipeline; nothing here is keyed.
  const railIcon = ({
    label,
    icon: Icon,
    onClick,
    disabled = false,
    hint,
    badge,
  }: {
    label: string;
    icon: LucideIcon;
    onClick: () => void;
    disabled?: boolean;
    /** Tooltip suffix, e.g. a shortcut or the reason it is disabled. */
    hint?: string;
    /** Small count pinned to the corner (custom-range selection). */
    badge?: number;
  }) => (
    <button
      type="button"
      aria-label={label}
      title={hint ? dc.withHint(label, hint) : label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-chat-text-muted transition-colors',
        'hover:bg-chat-surface-hover hover:text-chat-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chat-accent/50',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-chat-text-muted',
      )}
    >
      <Icon size={17} strokeWidth={1.9} aria-hidden />
      {badge !== undefined && badge > 0 && (
        <span
          aria-hidden
          className="absolute -right-0.5 -top-0.5 min-w-[14px] rounded-full bg-chat-accent px-1 text-center text-[9px] font-semibold leading-[14px] text-chat-on-accent"
        >
          {badge}
        </span>
      )}
    </button>
  );

  // #1393: one surface for the three modals â the brand’s surface tokens over
  // shadcn’s `bg-background`, the composer’s 16px radius, the drawer’s ink.
  const dialogSurface = cn(
    'gap-0 rounded-editorial border-chat-border bg-chat-surface p-0 text-chat-text shadow-chat-md',
    hostedLayer,
  );
  const sectionLabel = 'wia-label m-0 mb-2';
  const segmented = (checked: boolean) =>
    cn(
      'flex-1 rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors',
      checked
        ? 'border-chat-accent bg-chat-surface-hover text-chat-text'
        : 'border-chat-border bg-transparent text-chat-text-muted hover:bg-chat-surface-hover hover:text-chat-text',
    );
  const rowAction =
    'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[13px] text-chat-text transition-colors hover:bg-chat-surface-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent';
  const exporting = exportingDocx || exportingPdf;
  // #1731 phase 7 (§10.8): `capabilities.export` finally does something. It
  // had been declared `true` in both adapters and read nowhere, so the rail
  // icon and the modal were unconditional.
  const canExport = chatConfig().capabilities.export;
  const serverFormats = chatConfig().serverExportFormats;
  const listSizeClass = 'text-[length:calc(12.5rem/17*var(--content-font-scale))]';


  return (
    <>
      {isMobile && isExpanded && (
        <div
          data-testid="drawer-backdrop"
          aria-hidden
          onClick={onClose}
          // TRF delta (shell stacking contract, ui-ux-guideline §2.10): on a
          // phone the open drawer is an overlay like the More drawer, so it
          // and this scrim take the z-[60] slot above the z-50 header. At z-40
          // the header's Theme and Sign out stayed live beside the drawer.
          className="fixed inset-0 z-[60] bg-chat-scrim"
        />
      )}
      <aside
        aria-label={dc.historyLabel}
        /* TRF delta 2026-09-07: a COLLAPSED rail is still in the tab order.
           `w-0 overflow-hidden` (desktop) and `-translate-x-full` (mobile)
           both hide paint without removing anything from the document, so a
           keyboard user tabbing off the composer walked into ~20 invisible
           controls — search, the five toolbar icons, every Recents row — with
           no way to see where focus was. The Hide button lives in there too,
           so the state was self-sealing: collapse it and you cannot tab back
           to the control that reopens it. `inert` removes the subtree from
           focus AND from the accessibility tree in one attribute, and it is
           the only one that survives the width transition (`hidden` would
           kill the animation, `tabIndex={-1}` does not descend). */
        inert={!isExpanded}
        data-side={side}
        className={cn(
          'relative flex shrink-0 flex-col overflow-hidden border-chat-border bg-chat-bg',
          anchoredRight ? 'border-l' : 'border-r',
          isMobile
            ? cn('fixed inset-y-0 z-[60] w-[280px] transition-transform duration-200 ease-out',
                 anchoredRight ? 'right-0' : 'left-0',
                 isExpanded ? 'translate-x-0' : anchoredRight ? 'translate-x-full' : '-translate-x-full')
            : cn(isResizing ? '' : 'transition-[width] duration-200 ease-out', isExpanded ? '' : 'w-0'),
        )}
        style={!isMobile && isExpanded ? { width: sidebarWidth } : undefined}
      >
        {!isMobile && isExpanded && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={dc.resizeLabel}
            onMouseDown={handleResizeMouseDown}
            className={cn(
              'absolute top-0 z-10 h-full w-1 cursor-col-resize select-none hover:bg-chat-accent/40',
              anchoredRight ? 'left-0' : 'right-0',
              isResizing && 'bg-chat-accent/60',
            )}
          />
        )}
        {/* #1393: the five-icon row. */}
        <div
          role="toolbar"
          aria-label={dc.toolsLabel}
          className="flex shrink-0 items-center gap-1 border-b border-chat-border px-3 py-2.5"
        >
          {railIcon({
            label: dc.newConversation,
            icon: Plus,
            onClick: () => { onNewConversation(); if (isMobile) onClose(); },
          })}
          {railIcon({
            label: dc.searchConversations,
            icon: Search,
            hint: '⌘K',
            onClick: () => setSearchOpen(true),
          })}
          {railIcon({
            label: dc.viewOptions,
            icon: SlidersHorizontal,
            hint: dc.viewOptionsHint,
            onClick: () => setViewOpen(true),
          })}
          {canExport && railIcon({
            label: dc.download,
            icon: Download,
            disabled: !hasMessages,
            hint: hasMessages ? dc.downloadHint : dc.downloadDisabledHint,
            badge: slicePreset === 'custom' ? customSelectionCount : undefined,
            onClick: () => setExportOpen(true),
          })}
          {/* #293: ending is one-shot — the icon leaves once the session has ended. */}
          {!sessionEnded &&
            railIcon({
              label: dc.endSession,
              icon: LogOut,
              disabled: !hasMessages,
              hint: hasMessages ? undefined : dc.endSessionDisabledHint,
              onClick: onEnd,
            })}
          {railTrailingAction && (
            <span className="ml-auto flex items-center">{railTrailingAction}</span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          <p className="wia-label m-0 mb-2 px-3">
            {dc.recents}
            {decisionMarkers?.recents && <>{' '}{decisionMarkers.recents}</>}
          </p>

          {isLoading && (
            <p className={cn('px-3', listSizeClass, 'text-chat-text-muted')}>{dc.loading}</p>
          )}

          {isError && (
            <p className={cn('px-3', listSizeClass, 'text-chat-text-muted')}>
              {dc.loadFailed}{' '}
              <button
                type="button"
                onClick={() => refetch()}
                className="bg-transparent underline transition-colors hover:text-chat-text"
              >
                {dc.retry}
              </button>
            </p>
          )}

          {conversations && conversations.length === 0 && (
            <p className={cn('px-3', listSizeClass, 'text-chat-text-muted')}>{dc.empty}</p>
          )}

          {conversations && conversations.length > 0 && recentsView === 'recent' && (
            // #1308 (O-4): recency buckets — Today · This week · Earlier;
            // empty groups don’t render. Header construction mirrors the
            // Topics view’s group headers.
            <div className="flex flex-col gap-3">
              {groupByRecency(conversations, (c) => c.lastActivity || c.createdAt, renderNow).map(
                (group) => (
                  <div key={group.key}>
                    <p className="m-0 mb-1 px-3 text-[length:calc(10.5rem/17*var(--content-font-scale))] uppercase tracking-wider text-chat-text-muted">
                      {dc.groups[group.key]}
                    </p>
                    <ul className="flex flex-col gap-0.5">
                      {group.items.map((c) => renderConversationRow(c))}
                    </ul>
                  </div>
                )
              )}
            </div>
          )}

          {conversations && conversations.length > 0 && recentsView === 'topics' && (
            // #473: grouped by primary keyTopic; untagged conversations land
            // in a trailing "Ungrouped" section (grouping logic is a pure
            // util — see conversation-topics.ts).
            <div className="flex flex-col gap-3">
              {groupConversationsByTopic(conversations, { ungroupedLabel: dc.ungrouped, locale: chatCopy().locale }).map((group) => (
                <div key={group.key || '__ungrouped'}>
                  <p className="m-0 mb-1 px-3 text-[length:calc(10.5rem/17*var(--content-font-scale))] uppercase tracking-wider text-chat-text-muted">
                    {group.label}
                  </p>
                  <ul className="flex flex-col gap-0.5">
                    {group.conversations.map((c) => renderConversationRow(c))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate(chatConfig().routes.chatHistory)}
            className="mt-3 inline-flex items-center gap-1 bg-transparent px-3 text-[12px] text-chat-text-muted transition-colors hover:text-chat-text"
          >
            {dc.viewAll}
          </button>
        </div>
      </aside>

      {rowPendingDelete && (
        // The confirm portals to <body>; its content and scrim both take the
        // z-[70] slot so it opens above the phone drawer.
        <AlertDialog open onOpenChange={(open) => !open && setRowPendingDelete(null)}>
          <AlertDialogContent
            overlayClassName={hostedLayer}
            className={hostedLayer}
            onCloseAutoFocus={(e) => {
              const trigger = rowMenuTriggerRef.current;
              if (trigger?.isConnected) {
                e.preventDefault();
                trigger.focus();
              }
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{dc.deleteTitle}</AlertDialogTitle>
              <AlertDialogDescription>{dc.deleteBody}</AlertDialogDescription>
              {decisionMarkers?.deleteDialog && <div>{decisionMarkers.deleteDialog}</div>}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingRow}>{dc.cancel}</AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeletingRow}
                onClick={handleConfirmRowDelete}
              >
                {isDeletingRow ? dc.deleting : dc.delete}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* ── #2404 · Rename ─────────────────────────────────────────────
          The backend takes 1–100 characters; a blank draft cannot be saved.
          A failure keeps the dialog and the draft, and says so. */}
      <Dialog open={rowPendingRename !== null} onOpenChange={(open) => { if (!open && !isRenamingRow) setRowPendingRename(null); }}>
        <DialogContent aria-describedby={undefined} overlayClassName={hostedLayer} className={cn(dialogSurface, 'sm:max-w-sm')}>
          <DialogHeader className="border-b border-chat-border px-5 py-4">
            <DialogTitle className="text-[15px] font-semibold">{dc.renameTitle}</DialogTitle>
            {decisionMarkers?.rename && <div>{decisionMarkers.rename}</div>}
          </DialogHeader>
          <form
            className="flex flex-col gap-3 px-5 py-4"
            onSubmit={(e) => { e.preventDefault(); void handleConfirmRename(); }}
          >
            <label htmlFor="drawer-rename-input" className={sectionLabel}>{dc.renameLabel}</label>
            <input
              id="drawer-rename-input"
              type="text"
              autoFocus
              maxLength={100}
              value={renameDraft}
              onChange={(e) => { setRenameDraft(e.target.value); setRenameFailed(false); }}
              aria-invalid={renameFailed || undefined}
              aria-describedby={renameFailed ? 'drawer-rename-error' : undefined}
              className="w-full rounded-lg border border-chat-border bg-transparent px-3 py-2 text-[15px] text-chat-text focus:border-chat-accent focus:outline-none"
            />
            {renameFailed && (
              <p id="drawer-rename-error" role="alert" className="m-0 text-[12.5px] text-destructive">
                {dc.renameFailed}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" disabled={isRenamingRow} onClick={() => setRowPendingRename(null)}>
                {dc.cancel}
              </Button>
              <Button type="submit" disabled={isRenamingRow || renameDraft.trim().length === 0}>
                {isRenamingRow ? dc.renameSaving : dc.renameSave}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── #1393 · Search ─────────────────────────────────────────────
          #553's title filter over the loaded recents, in a command-palette
          shape: field on top, hits below, Esc closes. Anchored near the top
          of the viewport (not centred) so the list can grow downward. */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          overlayClassName={hostedLayer}
          className={cn(dialogSurface, 'top-[12vh] translate-y-0 sm:max-w-md')}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{dc.searchConversations}</DialogTitle>
          </DialogHeader>
          <div className="relative border-b border-chat-border">
            <Search
              size={15}
              aria-hidden
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-chat-text-muted"
            />
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={dc.searchConversations}
              aria-label={dc.searchConversations}
              className="w-full rounded-t-2xl bg-transparent py-3.5 pl-11 pr-11 text-[15px] text-chat-text placeholder:text-chat-text-muted focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                aria-label={dc.clearSearch}
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-chat-text-muted transition-colors hover:bg-chat-surface-hover hover:text-chat-text"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="max-h-[52vh] overflow-y-auto p-2">
            {conversations && conversations.length === 0 && (
              <p className={cn('px-3 py-2', listSizeClass, 'text-chat-text-muted')}>{dc.empty}</p>
            )}
            {/* #553: search matched nothing among the loaded conversations */}
            {conversations && conversations.length > 0 && visibleConversations
              && visibleConversations.length === 0 && (
              <p className={cn('px-3 py-2', listSizeClass, 'text-chat-text-muted')}>
                {dc.noMatches(searchQuery.trim())}
              </p>
            )}
            {visibleConversations && visibleConversations.length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {visibleConversations.map((c) => renderConversationRow(c, { inDialog: true }))}
              </ul>
            )}
          </div>
          <p className="border-t border-chat-border px-4 py-2 text-[11px] text-chat-text-muted">
            {dc.searchFooter(conversations?.length ?? 0)}
          </p>
        </DialogContent>
      </Dialog>

      {/* ── #1393 · View options ───────────────────────────────────────
          #473 Recent/Topics and the Aa text-size control, as two labelled
          segmented rows. Both persist exactly as before (localStorage). */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent
          aria-describedby={undefined}
          overlayClassName={hostedLayer}
          className={cn(dialogSurface, 'sm:max-w-sm')}
        >
          <DialogHeader className="border-b border-chat-border px-5 py-4">
            <DialogTitle className="text-[15px] font-semibold">{dc.viewOptions}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 px-5 py-4">
            <div>
              <p className={sectionLabel}>{dc.recents}</p>
              <div role="radiogroup" aria-label={dc.recentsView} className="flex gap-1.5">
                {RECENTS_VIEWS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={recentsView === value}
                    onClick={() => handleRecentsViewChange(value)}
                    className={segmented(recentsView === value)}
                  >
                    {dc.views[value]}
                  </button>
                ))}
              </div>
              <p className="m-0 mt-1.5 text-[11.5px] text-chat-text-muted">
                {dc.viewHints[recentsView]}
              </p>
            </div>
            <div>
              {/* Backlog 2026-07-13: Aa text-size control (Small/Medium/Large)
                  — see useFontScale.ts. Body text only; chrome stays fixed. */}
              <p className={sectionLabel}>{dc.textSize}</p>
              <div role="radiogroup" aria-label={dc.textSize} className="flex gap-1.5">
                {FONT_SCALES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={fontScale === value}
                    onClick={() => setFontScale(value)}
                    className={segmented(fontScale === value)}
                  >
                    {dc.fontScales[value]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── #1393 · This conversation ──────────────────────────────────
          The old bottom block, whole: range (#270/#269) → formats (#554,
          #268, #275, CP-36) → copy → delete. 'Custom' closes the modal so
          the messages behind it can be clicked; the Download icon then wears
          the selection count until the user comes back. */}
      <Dialog open={canExport && exportOpen} onOpenChange={setExportOpen}>
        <DialogContent
          overlayClassName={hostedLayer}
          className={cn(dialogSurface, 'sm:max-w-md')}
        >
          <DialogHeader className="border-b border-chat-border px-5 py-4">
            <DialogTitle className="text-[15px] font-semibold">{dc.exportTitle}</DialogTitle>
            {/* #472/#554: the explainer precedes the range control */}
            <DialogDescription className="text-[12.5px] text-chat-text-muted">
              {dc.exportDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 px-5 py-4">
            <div>
              <p className={sectionLabel}>{dc.range}</p>
              <div role="radiogroup" aria-label={dc.rangeLabel} className="flex gap-1.5">
                {SLICE_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={slicePreset === value}
                    onClick={() => {
                      changeSlicePreset(value);
                      if (value === 'custom') setExportOpen(false);
                    }}
                    className={segmented(slicePreset === value)}
                  >
                    {dc.slices[value]}
                  </button>
                ))}
              </div>
              {sliceEmpty && (
                <p className="m-0 mt-1.5 text-[11.5px] text-chat-text-muted">
                  {slicePreset === 'custom' ? dc.customEmpty : dc.rangeEmpty}
                </p>
              )}
            </div>
            <div>
              <p className={sectionLabel}>{dc.downloadAs}</p>
              <div className="grid grid-cols-2 gap-1">
                <button type="button" className={rowAction} disabled={exporting || sliceEmpty} onClick={() => onDownload('md', slicePreset)}>
                  {dc.formats.markdown}
                </button>
                <button type="button" className={rowAction} disabled={exporting || sliceEmpty} onClick={() => onDownload('txt', slicePreset)}>
                  {dc.formats.plainText}
                </button>
                {/* #268/#275: server-rendered documents. These need an
                    endpoint, so they are drawn only where the surface's
                    `serverExportFormats` says one answers (#1731 phase 7).
                    A button whose only behaviour is to apologise is the
                    half-surface §10.8 warns about. */}
                {serverFormats.includes('docx') && (
                  <button type="button" className={rowAction} disabled={exporting || sliceEmpty} onClick={() => onDownload('docx', slicePreset)}>
                    {exportingDocx ? dc.formats.preparingWord : dc.formats.word}
                  </button>
                )}
                {serverFormats.includes('pdf') && (
                  <button type="button" className={rowAction} disabled={exporting || sliceEmpty} onClick={() => onDownload('pdf', slicePreset)}>
                    {exportingPdf ? dc.formats.preparingPdf : dc.formats.pdf}
                  </button>
                )}
                {/* #1308 (CP-36): client-side print path â the browser’s own
                    print dialog, "Save as PDF" there. No server renderer. */}
                <button type="button" className={cn(rowAction, 'col-span-2')} disabled={exporting || sliceEmpty} onClick={() => onDownload('print', slicePreset)}>
                  {chatCopy().export.printPdf}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onCopy(slicePreset)}
              disabled={copying || sliceEmpty}
              className={cn(rowAction, 'border border-chat-border')}
            >
              {copied ? dc.copied : slicePreset === 'everything' ? dc.copyAll : dc.copyRange}
            </button>
          </div>
          <div className="border-t border-chat-border px-5 py-3">
            <button
              type="button"
              onClick={() => { setExportOpen(false); onDelete(); }}
              className="flex w-full items-center justify-start rounded-lg px-3 py-2 text-left text-[13px] text-destructive transition-colors hover:bg-destructive/10"
            >
              {dc.deleteThread}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
